import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { setClientIp } from './utils/gtm';
import { pushDataLayer, getTrackingCookies } from './utils/gtm';
import { buildFinalPayload, buildHkdkPayload } from './utils/wompi-payload';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import SuccessScreen from './components/SuccessScreen';
import Footer from './components/Footer';
import StickyBar from './components/StickyBar';
import AddAnotherPopup from './components/AddAnotherPopup';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import { AppStep, Query, RequesterInfo, VerifyStatus } from './types';

type Page = 'main' | 'terms' | 'privacy';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

function App() {
  const [currentStep, setCurrentStep] = useState<AppStep>(1);
  const [queries, setQueries] = useState<Query[]>([]);
  const [requesterInfo, setRequesterInfo] = useState<RequesterInfo | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [page, setPage] = useState<Page>('main');
  const [showStep1Form, setShowStep1Form] = useState(true);
  const [verifyTrigger, setVerifyTrigger] = useState(0);
  const [showAddAnotherPopup, setShowAddAnotherPopup] = useState(false);
  const [removedQueryError, setRemovedQueryError] = useState<string | null>(null);

  // Fetch client IP once on mount so GTM events include it
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${API_URL}/api/client-info`)
      .then(res => setClientIp(res.data.ip))
      .catch(() => {});
  }, []);

  // ── PSE / Nequi redirect recovery ────────────────────────────────────────
  // Wompi appends ?id=TRANSACTION_ID when redirecting back after async payment.
  // We verify the transaction, fire all webhooks + GTM, then show success.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const transactionId = params.get('id');
    if (!transactionId) return;

    const pendingStr = sessionStorage.getItem('wompi_pending');
    if (!pendingStr) return;

    let pending: { reference: string; queries: Query[]; requesterInfo: RequesterInfo };
    try { pending = JSON.parse(pendingStr); } catch { return; }

    const API_URL = process.env.REACT_APP_BACKEND_URL || '';

    // Clean URL immediately so a page refresh doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);
    sessionStorage.removeItem('wompi_pending');

    axios.get(`${API_URL}/api/verify-wompi-transaction?transaction_id=${transactionId}`)
      .then(async res => {
        const { status, reference } = res.data;
        if (status !== 'APPROVED' || reference !== pending.reference) return;

        const { queries: qs, requesterInfo: ri } = pending;

        // GTM purchase event
        pushDataLayer('purchase', {
          currency: 'COP',
          value: qs.reduce((s: number, q: Query) => s + q.amount, 0),
          transaction_id: transactionId,
          user: { first_name: ri.firstName, last_name: ri.lastName, email: ri.email, phone: ri.phone },
          items: qs.map((q: Query) => ({ doc_type: q.docType, doc_number: q.docNumber, price: q.amount })),
          tracking: getTrackingCookies(),
        });

        // n8n webhook
        try {
          await axios.post(`${API_URL}/api/submit-order`,
            { data: buildFinalPayload(qs, ri, reference).data }, { timeout: 10000 });
        } catch (_) {}

        // hkdk webhook — one per person
        await Promise.all(qs.map((q: Query) =>
          axios.post(`${API_URL}/api/submit-hkdk`, buildHkdkPayload(q, ri, transactionId), { timeout: 10000 })
            .catch(() => {})
        ));

        // Restore state and show success
        setQueries(qs);
        setRequesterInfo(ri);
        setShowSuccess(true);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-restore form when all queries removed
  useEffect(() => {
    if (queries.length === 0) setShowStep1Form(true);
  }, [queries.length]);

  const addQuery = (query: Query) => setQueries(prev => [...prev, query]);
  const removeQuery = (id: string) => setQueries(prev => prev.filter(q => q.id !== id));
  const totalAmount = queries.reduce((sum, q) => sum + q.amount, 0);

  const handleReset = () => {
    setShowSuccess(false);
    setCurrentStep(1);
    setQueries([]);
    setRequesterInfo(null);
  };

  const startBackgroundVerify = (query: Query) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const payload = {
      data: {
        hidden_form_id: { name: 'hidden_form_id', value: '916', type: 'form_id' },
        tipo_documento: {
          name: 'tipo_documento', value: query.docType, label: 'Tipo de documento:',
          exclude: 0, type: 'var', option_label: query.docTypeLabel,
        },
        numero_documento: {
          name: 'numero_documento', value: query.docNumber,
          label: 'Número de documento:', exclude: 0, type: 'var',
        },
        valor: { name: 'valor', value: '99000', label: 'Oculto:', type: 'var' },
        'motivo-consulta': {
          name: 'motivo-consulta', value: query.motivo, label: 'Motivo de consulta:',
          exclude: 0, type: 'var', option_label: query.motivoLabel,
        },
      },
    };
    axios.post(`${API_URL}/api/verify-person`, payload, { timeout: 240000 })
      .then(res => {
        const data = res.data;
        if (Array.isArray(data) && data.length > 0 && data[0]?.nombreCompleto) {
          setQueries(prev => prev.map(q =>
            q.id === query.id
              ? { ...q, nombreCompleto: data[0].nombreCompleto, verifyStatus: 'verified' as VerifyStatus }
              : q
          ));
        } else {
          handleVerifyNotFound(query);
        }
      })
      .catch(() => handleVerifyNotFound(query));
  };

  const handleVerifyNotFound = (query: Query) => {
    if (query.docType === 'CC' || query.docType === 'CE') {
      setQueries(prev => prev.filter(q => q.id !== query.id));
      setRemovedQueryError(
        `No fue posible verificar el documento ${query.docTypeLabel} ${query.docNumber}. Por favor verifica el tipo y número de documento e inténtalo de nuevo.`
      );
      setCurrentStep(1);
      setShowStep1Form(true);
      setShowAddAnotherPopup(false);
    } else {
      setQueries(prev => prev.map(q =>
        q.id === query.id ? { ...q, verifyStatus: 'not-found' as VerifyStatus } : q
      ));
    }
  };

  if (showSuccess) {
    return <SuccessScreen email={requesterInfo?.email || ''} onReset={handleReset} />;
  }

  // Legal pages overlay
  if (page === 'terms') {
    return <TermsPage onBack={() => setPage('main')} />;
  }
  if (page === 'privacy') {
    return <PrivacyPage onBack={() => setPage('main')} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)', fontFamily: 'Inter, sans-serif' }}>
      <Header currentStep={currentStep} />

      <main className="pb-28">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div key="step1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4, ease: 'easeOut' }}>
              {/* #hero-visibility: hide hero when person(s) added */}
              <AnimatePresence>
                {queries.length === 0 && (
                  <motion.div
                    key="hero"
                    initial={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <HeroSection />
                  </motion.div>
                )}
              </AnimatePresence>
              <Step1
                queries={queries}
                onAddQuery={addQuery}
                onRemoveQuery={removeQuery}
                showForm={showStep1Form}
                onSetShowForm={setShowStep1Form}
                verifyTrigger={verifyTrigger}
                onVerifyBackground={startBackgroundVerify}
                onRequesterInfoCaptured={(info) => setRequesterInfo(info)}
                onShowAddAnotherPopup={() => setShowAddAnotherPopup(true)}
                removedQueryError={removedQueryError}
                onClearRemovedQueryError={() => setRemovedQueryError(null)}
              />
            </motion.div>
          )}
          {currentStep === 2 && requesterInfo && (
            <motion.div key="step2" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4, ease: 'easeOut' }}>
              <Step2
                queries={queries}
                requesterInfo={requesterInfo}
                onSubmit={(info) => { setRequesterInfo(info); setCurrentStep(3); }}
                onBack={() => setCurrentStep(1)}
              />
            </motion.div>
          )}
          {currentStep === 3 && requesterInfo && (
            <motion.div key="step3" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4, ease: 'easeOut' }}>
              <Step3
                queries={queries}
                requesterInfo={requesterInfo}
                onSuccess={() => setShowSuccess(true)}
                onBack={() => setCurrentStep(2)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {currentStep === 1 && queries.length > 0 && !showAddAnotherPopup && (
          <StickyBar
            count={queries.length}
            total={totalAmount}
            onContinue={() => setCurrentStep(2)}
            onAddAnother={() => setShowAddAnotherPopup(true)}
            showAddAnother={!showStep1Form}
            verifyMode={false}
          />
        )}
      </AnimatePresence>

      <AddAnotherPopup
        isOpen={showAddAnotherPopup}
        existingDocNumbers={queries.map(q => q.docNumber)}
        onAddAnother={(query, _verifyFn) => {
          addQuery(query);
          startBackgroundVerify(query);
        }}
        onDone={() => {
          setShowAddAnotherPopup(false);
          setCurrentStep(2);
        }}
      />

      <Footer onTerms={() => setPage('terms')} onPrivacy={() => setPage('privacy')} />
    </div>
  );
}

export default App;
